"use client";

import React, { useRef, useEffect } from "react";

// Horizontal flex container that scrolls on mobile via native touch
// pan AND on desktop via mouse click+drag. The `onClickInside` lets
// the caller stop click propagation to the row's outer onClick
// handler. Drags >5px swallow the synthetic click so a drag doesn't
// accidentally select a chip the cursor lands on.
export function DragScrollRow({
  className,
  children,
  onClickInside,
}: {
  className?: string;
  children: React.ReactNode;
  onClickInside?: (e: React.MouseEvent<HTMLDivElement>) => void;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const draggingRef = useRef(false);
  const startXRef = useRef(0);
  const startScrollRef = useRef(0);
  const movedRef = useRef(false);

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    const el = ref.current;
    if (!el) return;
    draggingRef.current = true;
    movedRef.current = false;
    startXRef.current = e.pageX;
    startScrollRef.current = el.scrollLeft;
  };

  useEffect(() => {
    const onMove = (ev: MouseEvent) => {
      if (!draggingRef.current || !ref.current) return;
      const dx = ev.pageX - startXRef.current;
      if (Math.abs(dx) > 4) {
        movedRef.current = true;
        ref.current.style.cursor = 'grabbing';
        ref.current.scrollLeft = startScrollRef.current - dx;
        ev.preventDefault();
      }
    };
    const onUp = () => {
      if (ref.current) ref.current.style.cursor = '';
      draggingRef.current = false;
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, []);

  const handleClickCapture = (e: React.MouseEvent<HTMLDivElement>) => {
    // Swallow the click if the user just dragged — keeps button onClicks
    // from firing when the user's only intent was to scroll.
    if (movedRef.current) {
      movedRef.current = false;
      e.preventDefault();
      e.stopPropagation();
    }
    onClickInside?.(e);
  };

  return (
    <div
      ref={ref}
      onMouseDown={handleMouseDown}
      onClickCapture={handleClickCapture}
      className={className}
      style={{ cursor: 'grab' }}
    >
      {children}
    </div>
  );
}
